import React, { VFC, useCallback, useState, useEffect } from 'react';
import {
  STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT,
  STEP_EDITOR_CONTENT_OPTION_INITIAL,
  STEP_EDITOR_CONTENT_OPTION_JOURNEY_DOCUMENT,
  STEP_EDITOR_CONTENT_OPTION_LIBRARY,
  STEP_EDITOR_CONTENT_OPTION_RECORD,
  STEP_EDITOR_CONTENT_OPTION_UPLOAD,
  STEP_EDITOR_CONTENT_OPTION_URL,
} from 'src/editor/constants';

import { useStepEditorContext } from 'src/store/step-editor';
import { useEditorContext } from 'src/store/editor';

import { StepEditorContentFileUpload } from '../file/upload';
import { StepEditorContentLibrary } from '../library';
import { StepEditorContentPreview } from '../preview';
import { StepEditorContentVideoRecorder } from '../video-recorder';
import StepEditorContentSearch from '../search';
import { EventBus } from 'src/common/EventBus';
import { StepEditorContentConfigurePanel } from '../configure/panel';
import { StepEditorContentConfigureStageTypeEnum } from '../configure/stage-type.enum';

import { apiUpdateContent, apiUpdateNodeContent } from 'src/utils/journeyApi';
import { EditorContentFormEmbedInput } from 'src/editor/content-form/embed-input';
import { functionNoop } from 'src/utils/function/noop';
import { Nullable } from 'src/types/nullable.type';
import { findNodeInStep, isPlaceholderNode } from 'src/utils/journey';
import { Content } from 'src/common/interfaces/content.interface';

export interface StepEditorContentFormBodyProps {
  activeStage: StepEditorContentConfigureStageTypeEnum;
  onEmbedUrlChange?: (url: string) => void;
  onLoomRecordingComplete: (title: string, embedUrl: string) => void;
}

export const StepEditorContentFormBody: VFC<StepEditorContentFormBodyProps> = ({
  activeStage,
  onEmbedUrlChange = functionNoop,
  onLoomRecordingComplete,
}) => {
  const {
    url,
    name,
    loading,
    node_id,
    content_id,
    destination,
    setName,
    setCurrentConfigurationStage,
    setPreviewVideoHTML,
    setContentDetails,
    setContentOption,
    setContentId,
    setContentName,
    // setStateFromNode,
    contentOption,
    uploadedS3Asset,
  } = useStepEditorContext();
  const { currentStep } = useEditorContext();
  const [existingContent, setExistingContent] = useState<Nullable<Content>>(null);

  const onEditContentClicked = useCallback(() => {
    EventBus.emit('editor:show-warning-content-panel');
  }, []);

  const currentNode = node_id && currentStep ? findNodeInStep(currentStep, node_id) : undefined;

  useEffect(() => {
    if (isPlaceholderNode(currentNode)) {
      EventBus.emit('editor:reset-stage', {
        showInitialStage: true,
        resetEditor: true,
        overwriteValues: {
          name: currentNode.name,
          isPlaceholder: true,
          placeholderNodeId: node_id,
          annotations: currentNode.annotations,
        },
      });
    }
  }, [currentNode]);

  const onChooseExistingContent = (content: Content) => {
    setContentDetails(content);
    if (!name) {
      setName(content.name);
    }
    setExistingContent(content);
    setContentId(content.id);
    setCurrentConfigurationStage(StepEditorContentConfigureStageTypeEnum.CONFIGURE);
  };

  const onContentNameUpdate = (name: string) => {
    const updateFn = content_id ? apiUpdateContent(content_id, { name }) : apiUpdateNodeContent(node_id, { name });

    return updateFn.then((content: Content) => {
      setContentName(content.name);
      setContentId(content.id);
      return content;
    });
  };

  switch (contentOption) {
    case STEP_EDITOR_CONTENT_OPTION_URL:
      if (!url && !destination) {
        return <StepEditorContentSearch onUrlChange={onEmbedUrlChange} onChooseExisting={onChooseExistingContent} />;
      } else if ((destination && !url) || (loading && (url || destination))) {
        return (
          <EditorContentFormEmbedInput
            destination={destination}
            url={url}
            onChange={onEmbedUrlChange}
            loading={loading}
            onCancel={() => {
              setContentOption(STEP_EDITOR_CONTENT_OPTION_INITIAL);
            }}
          />
        );
      } else {
        return (
          <>
            {activeStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE && (
              <StepEditorContentPreview
                node={currentNode}
                onEditClick={onEditContentClicked}
                onContentNameUpdate={onContentNameUpdate}
              />
            )}
            <StepEditorContentConfigurePanel activeStage={activeStage} />
          </>
        );
      }
    case STEP_EDITOR_CONTENT_OPTION_LIBRARY:
      return <StepEditorContentLibrary onChooseExisting={onChooseExistingContent} />;
    case STEP_EDITOR_CONTENT_OPTION_RECORD:
      return (
        <>
          {
            <StepEditorContentVideoRecorder
              activeStage={activeStage}
              onLoomRecordingComplete={onLoomRecordingComplete}
              onPreviewReady={setPreviewVideoHTML}
            />
          }
          {uploadedS3Asset && <StepEditorContentConfigurePanel activeStage={activeStage} />}
        </>
      );
    case STEP_EDITOR_CONTENT_OPTION_UPLOAD:
      return (
        <>
          <StepEditorContentFileUpload />
          {uploadedS3Asset && <StepEditorContentConfigurePanel activeStage={activeStage} />}
        </>
      );
    case STEP_EDITOR_CONTENT_OPTION_JOURNEY_DOCUMENT:
      return (
        <>
          <StepEditorContentPreview
            node={existingContent || currentNode}
            onEditClick={onEditContentClicked}
            onContentNameUpdate={onContentNameUpdate}
          />
          <StepEditorContentConfigurePanel activeStage={activeStage} />
        </>
      );
    case STEP_EDITOR_CONTENT_OPTION_EXISTING_CONTENT:
      return (
        <>
          {activeStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE && (
            <StepEditorContentPreview
              node={existingContent || currentNode}
              onEditClick={onEditContentClicked}
              onContentNameUpdate={onContentNameUpdate}
            />
          )}
          <StepEditorContentConfigurePanel activeStage={activeStage} />
        </>
      );

    default:
      return null;
  }
};
